/**
 * @krisspy-file
 * @type component
 * @name "AddTouchpointModal"
 * @description "Modální okno pro přidání vlastního touchpointu: název + typ (digitální/fyzický)"
 */

import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';

interface AddTouchpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (touchpoint: { name: string; type: 'digital' | 'physical' }) => void;
  phaseId?: string;
  phaseName?: string;
  defaultType?: 'digital' | 'physical';
}

const AddTouchpointModal: React.FC<AddTouchpointModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  phaseId,
  phaseName,
  defaultType = 'digital',
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'digital' | 'physical'>(defaultType);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName('');
      setType(defaultType);
      setError('');
    }
  }, [isOpen, defaultType]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Název touchpointu je povinný');
      return;
    }
    onAdd({ name: name.trim(), type });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Přidat touchpoint</h2>
            {phaseName && (
              <p className="text-xs text-gray-400 mt-0.5">Fáze: {phaseName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Název touchpointu *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              autoFocus
              placeholder="např. Zákaznická linka, Mobilní aplikace..."
              className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none transition-all placeholder-gray-400
                ${error ? 'border-red-400 focus:ring-2 focus:ring-red-300' : 'border-gray-300 focus:ring-2 focus:ring-indigo-400 focus:border-transparent'}`}
            />
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>

          {/* Type selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Typ
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType('digital')}
                className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all
                  ${type === 'digital'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  }`}
              >
                Digitální
              </button>
              <button
                type="button"
                onClick={() => setType('physical')}
                className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all
                  ${type === 'physical'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  }`}
              >
                Fyzický
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-400">
              {type === 'digital'
                ? 'Web, aplikace, email, sociální sítě, chatbot…'
                : 'Prodejna, call centrum, tištěné materiály…'}
            </p>
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Přidat
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTouchpointModal;
